import { useCallback, useEffect, useRef, useState } from 'react';
import { LANGUAGE_PREFERENCES_VERSION } from './constants';
import { languagePreferencesService } from './service';
import {
  readLanguagePreferencesFromStorage,
  writeLanguagePreferencesToStorage,
} from './storage';
import { type LanguageCode, type LanguagePreferences } from './types';

interface UseLanguagePreferencesOptions {
  userId?: string;
  debounceMs?: number;
  onSyncError?: () => void;
}

const withUpdatedAt = (preferences: LanguagePreferences): LanguagePreferences => ({
  ...preferences,
  version: preferences.version || LANGUAGE_PREFERENCES_VERSION,
  updatedAt: new Date().toISOString(),
});

export const applyLanguageToDocument = (languageCode: LanguageCode) => {
  const root = document.documentElement;
  root.lang = languageCode;
  root.dir = 'ltr';
};

export const initializeLanguageOnBootstrap = () => {
  const cached = readLanguagePreferencesFromStorage();
  applyLanguageToDocument(cached.languageCode);
  return cached;
};

export const useLanguagePreferences = ({
  userId,
  debounceMs = 500,
  onSyncError,
}: UseLanguagePreferencesOptions) => {
  const [preferences, setPreferences] = useState<LanguagePreferences>(() =>
    readLanguagePreferencesFromStorage()
  );
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const localMutationRef = useRef(false);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSyncRef = useRef<LanguagePreferences | null>(null);

  const applyPreferences = useCallback((next: LanguagePreferences, markAsMutated: boolean) => {
    const normalized = writeLanguagePreferencesToStorage(withUpdatedAt(next));
    setPreferences(normalized);
    if (markAsMutated) {
      localMutationRef.current = true;
    }
    return normalized;
  }, []);

  const syncToBackend = useCallback(
    async (next: LanguagePreferences) => {
      if (!userId) {
        return;
      }
      setIsSyncing(true);
      try {
        const saved = await languagePreferencesService.update(next);
        applyPreferences(saved, false);
        pendingSyncRef.current = null;
      } catch {
        onSyncError?.();
      } finally {
        setIsSyncing(false);
      }
    },
    [applyPreferences, onSyncError, userId]
  );

  const scheduleSync = useCallback(
    (next: LanguagePreferences) => {
      pendingSyncRef.current = next;
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
      syncTimerRef.current = setTimeout(() => {
        if (pendingSyncRef.current) {
          void syncToBackend(pendingSyncRef.current);
        }
      }, debounceMs);
    },
    [debounceMs, syncToBackend]
  );

  useEffect(() => {
    applyLanguageToDocument(preferences.languageCode);
  }, [preferences.languageCode]);

  useEffect(() => {
    const cached = readLanguagePreferencesFromStorage();
    setPreferences(cached);
    setIsHydrated(true);

    if (!userId) {
      return;
    }

    localMutationRef.current = false;
    let cancelled = false;

    const loadFromBackend = async () => {
      try {
        const remote = await languagePreferencesService.get();
        if (cancelled || localMutationRef.current) {
          return;
        }
        applyPreferences(remote, false);
      } catch {
        return;
      }
    };

    void loadFromBackend();

    return () => {
      cancelled = true;
    };
  }, [applyPreferences, userId]);

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, []);

  const setLanguageCode = useCallback(
    (languageCode: LanguageCode) => {
      const next = applyPreferences(
        {
          ...preferences,
          languageCode,
        },
        true
      );
      scheduleSync(next);
    },
    [applyPreferences, preferences, scheduleSync]
  );

  const retryPendingSync = useCallback(() => {
    if (pendingSyncRef.current) {
      void syncToBackend(pendingSyncRef.current);
    }
  }, [syncToBackend]);

  return {
    preferences,
    languageCode: preferences.languageCode,
    isHydrated,
    isSyncing,
    setLanguageCode,
    retryPendingSync,
  };
};
